import { Component } from '@theme/component';

// Shopify renders all amounts and available plans; this component only coordinates interactions.
class HelloFloraCart extends Component {
  busy = false;
  pendingLine = null;
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleOpen);
    document.addEventListener('submit', this.handleAdd);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleOpen);
    document.removeEventListener('submit', this.handleAdd);
  }
  get body() { return this.querySelector('[data-hf-cart-content]'); }
  get drawer() { return this.closest('theme-drawer'); }
  get modal() { return this.querySelector('.hf-cart-modal'); }
  get strings() { return JSON.parse(this.querySelector('[data-hf-cart-strings]').textContent); }
  get root() { return this.body.dataset.root; }
  money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.body.dataset.currency }).format(cents / 100); }
  text(key, values) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[[${name}]]`, value), this.strings[key]); }
  error(message) { const target = this.querySelector('[data-hf-cart-error]'); if (target) target.textContent = message; }
  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.modal.open && !event.isComposing) {
      event.preventDefault();
      event.stopPropagation();
      this.modal.close();
    }
  };
  async render(html) {
    if (!html) throw new Error(this.strings.error);
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hf-cart-content]');
    if (!next) throw new Error(this.strings.error);
    this.body.replaceWith(next);
    document.querySelectorAll('[data-hf-cart-count]').forEach(node => node.textContent = next.dataset.count);
  }
  async refresh() {
    const response = await fetch(`${this.root}?section_id=hf-cart-content`, { cache: 'no-store' });
    if (!response.ok) throw new Error(this.strings.error);
    await this.render(await response.text());
  }
  handleOpen = async (event) => {
    const link = event.target.closest('[data-hf-open-cart]');
    if (!link || !this.drawer || event.ctrlKey || event.metaKey || event.shiftKey) return;
    event.preventDefault();
    this.drawer.open();
    if (this.busy) return;
    try { await this.refresh(); this.querySelector('[data-hf-close]')?.focus(); } catch { this.error(this.strings.error); }
  };
  handleAdd = async (event) => {
    const form = event.target;
    if (this.contains(form) && form.matches('.hf-cart-checkout-form') && this.busy) { event.preventDefault(); return; }
    if (!this.drawer || !form.matches('[data-hf-cart-form]') || event.defaultPrevented) return;
    event.preventDefault();
    if (this.busy) return;
    const submit = form.querySelector('[type="submit"]');
    const message = form.querySelector('[data-hf-message]');
    if (submit?.disabled) return;
    this.busy = true;
    if (submit) submit.disabled = true;
    if (message) message.textContent = '';
    const data = new FormData(form);
    data.set('sections', 'hf-cart-content');
    data.set('sections_url', this.root);
    try {
      const response = await fetch(`${this.root}cart/add.js`, { method: 'POST', body: data });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || this.strings.error);
      // Never retry an add automatically: a failed render must not add the item twice.
      if (result.sections?.['hf-cart-content']) await this.render(result.sections['hf-cart-content']);
      else await this.refresh();
      this.drawer.open();
      this.querySelector('[data-hf-close]')?.focus();
    } catch (error) {
      if (message) message.textContent = error.message;
    } finally {
      this.busy = false;
      if (submit) submit.disabled = false;
    }
  };
  async change(line, quantity, plan) {
    if (this.busy) return;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.error('');
    const data = { id: line.dataset.key, quantity, sections: ['hf-cart-content'], sections_url: this.root };
    if (plan !== undefined) data.selling_plan = plan;
    try {
      const response = await fetch(`${this.root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.strings.error);
      this.modal.close();
      if (result.sections?.['hf-cart-content']) await this.render(result.sections['hf-cart-content']);
      else await this.refresh();
      // Updating a price/plan clears the acknowledgment, so the shopper sees the new terms.
      this.querySelector('.hf-cart-heading h2')?.setAttribute('tabindex', '-1');
      this.querySelector('.hf-cart-heading h2')?.focus({ preventScroll: true });
    } catch (error) {
      this.modal.close();
      this.error(error.message);
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }
  handleClick = (event) => {
    const button = event.target.closest('button');
    if (!button || this.busy) return;
    if (button.hasAttribute('data-hf-close')) { this.drawer?.close(); return; }
    if (button.hasAttribute('data-hf-keep')) { this.modal.close(); return; }
    if (button.hasAttribute('data-hf-confirm-switch')) { if (this.pendingLine?.isConnected) this.change(this.pendingLine, Number(this.pendingLine.dataset.quantity), null); return; }
    const line = button.closest('[data-key]');
    if (!line) return;
    const count = Number(line.dataset.quantity);
    const subscribed = line.dataset.subscription === 'true';
    if (button.hasAttribute('data-hf-remove')) { this.change(line, 0); return; }
    if (button.dataset.hfQuantity) {
      const quantity = subscribed ? (button.dataset.hfQuantity === 'up' ? 3 : 1) : Math.max(1, count + (button.dataset.hfQuantity === 'up' ? 1 : -1));
      const plan = subscribed ? Number(quantity === 3 ? line.dataset.quarterly : line.dataset.monthly) : undefined;
      if (subscribed && !plan) { this.error(this.strings.error); return; }
      this.change(line, quantity, plan);
      return;
    }
    if (button.hasAttribute('data-hf-switch')) {
      if (!subscribed) {
        const quantity = count >= 3 ? 3 : 1;
        const plan = Number(quantity === 3 ? line.dataset.quarterly : line.dataset.monthly);
        if (!plan) { this.error(this.strings.error); return; }
        this.change(line, quantity, plan);
        return;
      }
      this.pendingLine = line;
      const base = Number(line.dataset.base);
      const total = Number(line.dataset.total);
      const savings = Math.max(0, base - total);
      this.modal.querySelector('[data-hf-price-change]').textContent = this.text('price_change', { difference: this.money(savings), total: this.money(base) });
      this.modal.querySelector('[data-hf-keep-label]').textContent = this.text('keep_discount', { percent: Math.round(savings / base * 100) });
      this.modal.querySelector('[data-hf-confirm-switch]').textContent = this.text('switch_once', { total: this.money(base) });
      this.modal.showModal();
    }
  };
}
if (!customElements.get('hf-cart-component')) customElements.define('hf-cart-component', HelloFloraCart);
